import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { colors } from "../../constants/Colors";
import { CompanyType } from "../../types/generalTypes";
import {
  updateDefaultWhatsAppMessage,
  getCompanyById,
} from "../../api/CompanyAPI";
import { getCompanyData } from "../../utils/generalUtil";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

interface DefaultWhatsAppMessageSettingsProps {
  company: CompanyType;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onCompanyUpdate: (company: CompanyType) => void;
}

const DefaultWhatsAppMessageSettings = ({
  company,
  onSuccess,
  onError,
  onCompanyUpdate,
}: DefaultWhatsAppMessageSettingsProps) => {
  const [message, setMessage] = useState(company.defaultWhatsAppMessage || "");
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const companyData = getCompanyData();
      const result = await updateDefaultWhatsAppMessage(
        companyData.companyId || 0,
        message,
      );

      if (!result.success) {
        onError(
          result.message || "Erro ao salvar mensagem padrão. Tente novamente.",
        );
        return;
      }

      const updatedCompany = await getCompanyById(
        companyData?.companyId?.toString() ?? "",
      );
      if (updatedCompany) {
        onCompanyUpdate(updatedCompany);
      }

      onSuccess("Mensagem padrão salva com sucesso!");
      setShowPreview(false);
    } catch (error) {
      onError("Erro ao salvar mensagem padrão. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setMessage("");
    setShowPreview(false);
    try {
      const companyData = getCompanyData();
      const result = await updateDefaultWhatsAppMessage(
        companyData.companyId || 0,
        "",
      );

      if (!result.success) {
        onError(
          result.message || "Erro ao limpar mensagem padrão. Tente novamente.",
        );
        return;
      }

      const updatedCompany = await getCompanyById(
        companyData?.companyId?.toString() ?? "",
      );
      if (updatedCompany) {
        onCompanyUpdate(updatedCompany);
      }

      onSuccess("Mensagem padrão removida com sucesso!");
    } catch (error) {
      onError("Erro ao limpar mensagem padrão. Tente novamente.");
    }
  };

  const characterCount = message.length;
  const maxCharacters = 1000;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, marginBottom: "16px", color: "#22303E" }}
      >
        Mensagem Padrão do WhatsApp
      </Typography>

      <Typography variant="body1" sx={{ marginBottom: "24px", color: "#666" }}>
        Configure uma mensagem personalizada que será enviada automaticamente
        aos clientes quando eles fizerem uma reserva. Esta mensagem será enviada
        antes da confirmação da reserva.
      </Typography>

      {!company.defaultWhatsAppMessage && (
        <Alert severity="info" sx={{ marginBottom: "24px" }}>
          Nenhuma mensagem padrão configurada. Os clientes receberão apenas a
          mensagem de confirmação da reserva.
        </Alert>
      )}

      <Box sx={{ marginBottom: "24px" }}>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            color: "#333",
            marginBottom: "12px",
          }}
        >
          Mensagem
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Digite sua mensagem personalizada aqui...&#10;&#10;Exemplo:&#10;Olá! Obrigado por escolher {nome_empresa}.&#10;Valorizamos muito sua reserva e estamos preparando tudo para receber você!"
          variant="outlined"
          disabled={saving}
          inputProps={{ maxLength: maxCharacters }}
          helperText={`${characterCount}/${maxCharacters} caracteres`}
          sx={{
            "& .MuiFormHelperText-root": {
              textAlign: "right",
              color: characterCount > maxCharacters * 0.9 ? "#d32f2f" : "#666",
            },
          }}
        />
      </Box>

      <Box sx={{ marginBottom: "24px" }}>
        <Button
          variant="outlined"
          onClick={() => setShowPreview(!showPreview)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: showPreview ? colors.blue : "inherit",
            color: showPreview ? colors.blue : "inherit",
          }}
        >
          {showPreview ? "Ocultar Preview" : "Ver Preview"}
        </Button>
      </Box>

      {showPreview && message && (
        <Paper
          elevation={3}
          sx={{
            padding: "24px",
            marginBottom: "24px",
            borderRadius: "12px",
            backgroundColor: "#f0fdf4",
            border: "1px solid #86efac",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 32, color: "#25D366", mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#166534",
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                  backgroundColor: "#dcf8c6",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  borderTopLeftRadius: "0",
                }}
              >
                {message || "Sua mensagem aparecerá aqui..."}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Box sx={{ display: "flex", gap: "16px" }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || characterCount === 0}
          sx={{
            backgroundColor: colors.blue,
            "&:hover": {
              backgroundColor: colors.darkBlue,
            },
            fontWeight: 550,
            padding: "12px 32px",
            fontSize: "16px",
          }}
        >
          {saving ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Salvar Mensagem"
          )}
        </Button>

        {company.defaultWhatsAppMessage && (
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={saving}
            sx={{
              borderColor: "#d32f2f",
              color: "#d32f2f",
              "&:hover": {
                borderColor: "#b71c1c",
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              },
              fontWeight: 550,
              padding: "12px 32px",
              fontSize: "16px",
            }}
          >
            Remover Mensagem
          </Button>
        )}
      </Box>

      <Box sx={{ marginTop: "32px" }}>
        <Alert severity="info">
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, marginBottom: "8px" }}
          >
            Como funciona:
          </Typography>
          <Typography variant="body2" component="div">
            <ol style={{ margin: 0, paddingLeft: "20px" }}>
              <li>
                A mensagem de confirmação da reserva será enviada
                automaticamente
              </li>
              <li> Em seguida, a mensagem padrão será enviada primeiro</li>
              <li>
                Se deixar vazia, apenas a confirmação da reserva será enviada
              </li>
              <li>Você pode usar emojis e formatação livre</li>
            </ol>
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default DefaultWhatsAppMessageSettings;
